import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../../../api/admin/adminApi';
import { useAdminTheme } from '../ui/useAdminTheme';
import { toUtcStartIso, toUtcEndExclusiveIso } from '../adminDateUtils';
import {
  analyticsPresetCalendarRange,
  chartPaletteForTheme,
  formatGrowthLabel,
} from './analyticsFormat';
import {
  clearStoredAnalyticsRange,
  readStoredAnalyticsRange,
  writeStoredAnalyticsRange,
} from './analyticsRangeStorage';

function rangeOptsFromStored(stored) {
  if (!stored) return null;
  if (stored.preset && stored.preset !== 'custom') {
    const r = analyticsPresetCalendarRange(stored.preset);
    return r ? { startIso: r.startIso, endIso: r.endIso, applied: r.applied, preset: stored.preset } : null;
  }
  if (stored.start && stored.end && stored.start <= stored.end) {
    const startIso = toUtcStartIso(stored.start);
    const endIso = toUtcEndExclusiveIso(stored.end);
    if (!startIso || !endIso) return null;
    return {
      startIso,
      endIso,
      applied: { start: stored.start, end: stored.end },
      preset: 'custom',
    };
  }
  return null;
}

export function useAdminAnalytics({ title, subtitle } = {}) {
  const outlet = useOutletContext() || {};
  const { registerShellMeta, setRefreshHandler } = outlet;
  const { theme } = useAdminTheme();

  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [growthRangeDraft, setGrowthRangeDraft] = useState({ start: '', end: '' });
  const [growthRangeApplied, setGrowthRangeApplied] = useState({ start: '', end: '' });
  const [growthRangeError, setGrowthRangeError] = useState('');
  const [analyticsRangePreset, setAnalyticsRangePreset] = useState(null);
  const [funnelTrendShowSubmits, setFunnelTrendShowSubmits] = useState(false);
  const [funnelTrendShowMerges, setFunnelTrendShowMerges] = useState(false);
  const dataRef = useRef(null);
  dataRef.current = analyticsData;
  const mountedRef = useRef(true);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const fetchAnalytics = useCallback(async (rangeOpts) => {
    const hasExisting = Boolean(dataRef.current);
    try {
      if (hasExisting) setRefreshing(true);
      else setLoading(true);
      if (mountedRef.current) setError(null);

      const params = {};
      if (rangeOpts?.startIso && rangeOpts?.endIso) {
        params.start = rangeOpts.startIso;
        params.end = rangeOpts.endIso;
      }

      const result = await adminApi.getAnalytics(params);
      if (!mountedRef.current) return;
      setAnalyticsData(result.data);
      setLastUpdatedAt(new Date());
      setIsAuthenticated(true);
    } catch (err) {
      if (!mountedRef.current) return;
      console.error('Error fetching analytics:', err);
      const statusCode = err.status || err.response?.status;
      if (statusCode === 403) {
        setIsAuthenticated(false);
        setError('You do not have admin access to this dashboard.');
      } else if (statusCode === 401) {
        setIsAuthenticated(false);
        setError('Your session has expired. Please sign in again.');
      } else if (!err.status && !err.response) {
        setError('Unable to connect to the server. Please check your internet connection and try again.');
      } else if (statusCode === 500) {
        setError('An unexpected error occurred. Please try again in a moment.');
      } else {
        setError('Failed to load analytics data. Please try again.');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  const applyStoredOrDefault = useCallback(async () => {
    const stored = readStoredAnalyticsRange();
    const restored = rangeOptsFromStored(stored);
    if (restored) {
      setGrowthRangeError('');
      setGrowthRangeApplied(restored.applied);
      setGrowthRangeDraft(restored.applied);
      setAnalyticsRangePreset(restored.preset === 'custom' ? 'custom' : restored.preset);
      await fetchAnalytics({ startIso: restored.startIso, endIso: restored.endIso });
      return;
    }
    await fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    applyStoredOrDefault();
    // Load once on mount; subsequent range changes call fetchAnalytics directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    registerShellMeta?.({
      title: title || 'Analytics',
      subtitle: subtitle || '',
      lastUpdatedAt,
    });
  }, [title, subtitle, lastUpdatedAt, registerShellMeta]);

  useEffect(() => {
    setRefreshHandler?.(() => applyStoredOrDefault);
  }, [setRefreshHandler, applyStoredOrDefault]);

  const applyFunnelPreset = async (presetKey) => {
    const r = analyticsPresetCalendarRange(presetKey);
    if (!r) return;
    setGrowthRangeError('');
    setGrowthRangeApplied(r.applied);
    setGrowthRangeDraft(r.applied);
    setAnalyticsRangePreset(presetKey);
    writeStoredAnalyticsRange({ preset: presetKey, start: r.applied.start, end: r.applied.end });
    await fetchAnalytics({ startIso: r.startIso, endIso: r.endIso });
  };

  const handleGrowthRangeDraftChange = (field) => (e) => {
    setGrowthRangeError('');
    setGrowthRangeDraft((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const applyGrowthRange = async () => {
    const { start, end } = growthRangeDraft;
    if (!start || !end) {
      setGrowthRangeError('Select both start and end dates');
      return;
    }
    if (start > end) {
      setGrowthRangeError('Start date must be before end date');
      return;
    }

    const startIso = toUtcStartIso(start);
    const endIso = toUtcEndExclusiveIso(end);
    if (!startIso || !endIso) {
      setGrowthRangeError('Invalid date range');
      return;
    }

    setGrowthRangeApplied({ start, end });
    setAnalyticsRangePreset('custom');
    writeStoredAnalyticsRange({ preset: 'custom', start, end });
    await fetchAnalytics({ startIso, endIso });
  };

  const clearGrowthRange = async () => {
    setGrowthRangeError('');
    setGrowthRangeDraft({ start: '', end: '' });
    setGrowthRangeApplied({ start: '', end: '' });
    setAnalyticsRangePreset(null);
    clearStoredAnalyticsRange();
    await fetchAnalytics();
  };

  const palette = useMemo(() => chartPaletteForTheme(theme), [theme]);

  const dailyUsersData = useMemo(() => ({
    labels: analyticsData?.growth?.daily?.map((item) => formatGrowthLabel(item._id)) || [],
    datasets: [{
      label: 'Daily New Users',
      data: analyticsData?.growth?.daily?.map((item) => item.count) || [],
      borderColor: palette.line,
      backgroundColor: palette.lineFill,
      tension: 0.1,
      fill: true,
    }],
  }), [analyticsData, palette]);

  const deviceStatsData = useMemo(() => {
    const devices = analyticsData?.devices?.devices || [];
    if (!devices.length) {
      return {
        labels: ['No data'],
        datasets: [{ label: 'Device Usage', data: [0], backgroundColor: palette.pie }],
        empty: true,
      };
    }
    return {
      labels: devices.map((item) => item._id || 'Unknown'),
      datasets: [{
        label: 'Device Usage',
        data: devices.map((item) => item.count),
        backgroundColor: palette.pie,
      }],
      empty: false,
    };
  }, [analyticsData, palette]);

  const guestFunnelDailyData = useMemo(() => {
    const funnelDailyRows = analyticsData?.guestFunnel?.dailySaves || [];
    return {
      labels: funnelDailyRows.map((d) => d.date),
      datasets: [
        {
          label: 'NFC card views',
          data: funnelDailyRows.map((d) => d.views || 0),
          borderColor: palette.pie[3],
          backgroundColor: 'transparent',
          tension: 0.2,
          borderDash: [5, 5],
          pointRadius: 2,
        },
        {
          label: 'Save to Velox opens',
          data: funnelDailyRows.map((d) => d.modal || 0),
          borderColor: palette.pie[1],
          backgroundColor: 'transparent',
          tension: 0.2,
          pointRadius: 2,
        },
        {
          label: 'Guest saves',
          data: funnelDailyRows.map((d) => d.saves || 0),
          borderColor: palette.line,
          backgroundColor: palette.lineFill,
          tension: 0.2,
          fill: true,
          pointRadius: 2,
        },
        ...(funnelTrendShowSubmits
          ? [{
              label: 'Email submits',
              data: funnelDailyRows.map((d) => d.emailSub || 0),
              borderColor: palette.pie[0],
              backgroundColor: 'transparent',
              tension: 0.2,
              pointRadius: 2,
              borderDash: [2, 3],
            }]
          : []),
        ...(funnelTrendShowMerges
          ? [{
              label: 'Merge attempts',
              data: funnelDailyRows.map((d) => d.mergeAttempts || 0),
              borderColor: palette.pie[4] || palette.pie[2],
              backgroundColor: 'transparent',
              tension: 0.2,
              pointRadius: 2,
            }]
          : []),
      ],
    };
  }, [analyticsData, palette, funnelTrendShowSubmits, funnelTrendShowMerges]);

  return {
    analyticsData,
    loading,
    refreshing,
    error,
    isAuthenticated,
    lastUpdatedAt,
    growthRangeDraft,
    growthRangeApplied,
    growthRangeError,
    analyticsRangePreset,
    funnelTrendShowSubmits,
    setFunnelTrendShowSubmits,
    funnelTrendShowMerges,
    setFunnelTrendShowMerges,
    fetchAnalytics,
    applyFunnelPreset,
    handleGrowthRangeDraftChange,
    applyGrowthRange,
    clearGrowthRange,
    palette,
    dailyUsersData,
    deviceStatsData,
    guestFunnelDailyData,
  };
}
